import { useState, useEffect, useCallback } from "react";
import type { Session, SessionWithLogs } from "../api/schemas";
import * as api from "../api/client";
import { formatTime, useStopwatch } from "../hooks/useStopwatch";

interface SessionCardProps {
    session: Session;
    onDelete: (id: string) => void;
    onUpdate: (session: Session) => void;
}

const STATUS_BORDER_CLASSES: Record<string, string> = {
    running: "border-start",
    paused: "border-pause",
    stopped: "border-transparent",
};

const STATUS_BADGE_CLASSES: Record<string, string> = {
    running: "bg-start text-black",
    paused: "bg-pause text-black",
    stopped: "bg-neutral-600 text-white",
};

const LOG_ACTION_CLASSES: Record<string, string> = {
    start: "bg-start text-black",
    pause: "bg-pause text-black",
    resume: "bg-resume text-black",
    stop: "bg-stop text-black",
};

export function SessionCard({ session, onDelete, onUpdate }: SessionCardProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [details, setDetails] = useState<SessionWithLogs | null>(null);
    const [showLogs, setShowLogs] = useState(false);
    const stopwatch = useStopwatch();

    const isStopped = session.status === "stopped";
    const isRunning = session.status === "running";
    const isPaused = session.status === "paused";

    useEffect(() => {
        if (!isRunning) return;
        loadDetailsAndSync();
    }, []);

    async function loadDetailsAndSync() {
        try {
            const data = await api.getSession(session.id);
            setDetails(data);
            const lastElapsed = computeLastElapsed(data);
            stopwatch.start(lastElapsed);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load session");
        }
    }

    function computeLastElapsed(data: SessionWithLogs): number {
        const lastLog = data.logs[data.logs.length - 1];
        if (!lastLog) return 0;
        return lastLog.elapsed_ms;
    }

    const handleStart = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const updated = await api.startSession(session.id, { elapsed_ms: 0 });
            stopwatch.start(0);
            onUpdate(updated);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to start session");
        } finally {
            setLoading(false);
        }
    }, [session.id, onUpdate, stopwatch]);

    const handlePause = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const updated = await api.pauseSession(session.id, { elapsed_ms: stopwatch.elapsedMs });
            stopwatch.pause();
            onUpdate(updated);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to pause session");
        } finally {
            setLoading(false);
        }
    }, [session.id, onUpdate, stopwatch]);

    const handleResume = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const updated = await api.resumeSession(session.id, { elapsed_ms: stopwatch.elapsedMs });
            stopwatch.resume();
            onUpdate(updated);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to resume session");
        } finally {
            setLoading(false);
        }
    }, [session.id, onUpdate, stopwatch]);

    const handleStop = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const updated = await api.stopSession(session.id, { elapsed_ms: stopwatch.elapsedMs });
            stopwatch.stop();
            onUpdate(updated);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to stop session");
        } finally {
            setLoading(false);
        }
    }, [session.id, onUpdate, stopwatch]);

    const handleDelete = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await api.deleteSession(session.id);
            onDelete(session.id);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to delete session");
            setLoading(false);
        }
    }, [session.id, onDelete]);

    const handleToggleLogs = useCallback(async () => {
        if (showLogs) {
            setShowLogs(false);
            return;
        }
        try {
            const data = await api.getSession(session.id);
            setDetails(data);
            setShowLogs(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load logs");
        }
    }, [session.id, showLogs]);

    const borderClass = STATUS_BORDER_CLASSES[session.status] ?? "border-transparent";
    const badgeClass = STATUS_BADGE_CLASSES[session.status] ?? "bg-neutral-600 text-white";

    return (
        <div className={`bg-card rounded-xl p-5 border-2 transition-colors ${borderClass}`}>
            <div className="flex justify-between items-center mb-3">
                <h3 className="m-0 text-xl font-semibold">{session.name}</h3>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${badgeClass}`}>
                    {session.status}
                </span>
            </div>

            <div className="text-center my-4">
                <span className="text-4xl font-bold tabular-nums text-primary">
                    {formatTime(stopwatch.elapsedMs)}
                </span>
            </div>

            {error && <p className="text-stop text-sm my-2">{error}</p>}

            <div className="flex gap-2 flex-wrap">
                {isStopped && (
                    <button
                        onClick={handleStart}
                        disabled={loading}
                        className="px-5 py-2.5 rounded-lg font-semibold text-sm bg-start text-black hover:brightness-110 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Start
                    </button>
                )}
                {isRunning && (
                    <>
                        <button
                            onClick={handlePause}
                            disabled={loading}
                            className="px-5 py-2.5 rounded-lg font-semibold text-sm bg-pause text-black hover:brightness-110 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Pause
                        </button>
                        <button
                            onClick={handleStop}
                            disabled={loading}
                            className="px-5 py-2.5 rounded-lg font-semibold text-sm bg-stop text-black hover:brightness-110 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Stop
                        </button>
                    </>
                )}
                {isPaused && (
                    <>
                        <button
                            onClick={handleResume}
                            disabled={loading}
                            className="px-5 py-2.5 rounded-lg font-semibold text-sm bg-resume text-black hover:brightness-110 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Resume
                        </button>
                        <button
                            onClick={handleStop}
                            disabled={loading}
                            className="px-5 py-2.5 rounded-lg font-semibold text-sm bg-stop text-black hover:brightness-110 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Stop
                        </button>
                    </>
                )}
                <button
                    onClick={handleToggleLogs}
                    disabled={loading}
                    className="px-5 py-2.5 rounded-lg font-semibold text-sm bg-transparent text-logs border border-logs hover:bg-logs hover:text-black transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {showLogs ? "Hide Logs" : "Logs"}
                </button>
                <button
                    onClick={handleDelete}
                    disabled={loading}
                    className="px-5 py-2.5 rounded-lg font-semibold text-sm bg-transparent text-stop border border-stop hover:bg-stop hover:text-black transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Delete
                </button>
            </div>

            {showLogs && details && (
                <div className="mt-4 pt-4 border-t border-neutral-700">
                    <h4 className="m-0 mb-3 text-base font-semibold">Session Logs</h4>
                    {details.logs.length === 0 && (
                        <p className="opacity-60 text-sm m-0">No logs yet</p>
                    )}
                    <ul className="list-none m-0 p-0 flex flex-col gap-2">
                        {details.logs.map((log) => (
                            <li
                                key={log.id}
                                className="flex gap-4 items-center px-3 py-2 bg-bg rounded-md text-sm"
                            >
                                <span
                                    className={`px-2 py-0.5 rounded text-xs font-semibold uppercase min-w-15 text-center ${LOG_ACTION_CLASSES[log.action] ?? ""}`}
                                >
                                    {log.action}
                                </span>
                                <span className="tabular-nums text-primary">
                                    {formatTime(log.elapsed_ms)}
                                </span>
                                <span className="opacity-60 ml-auto">
                                    {new Date(log.created_at).toLocaleTimeString()}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
